import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { API } from '../../../../api';
import { celebrar } from './celebracion';
import { trozosGrande } from './quesitos';
import ProgramaGuiado from './ProgramaGuiado';
import MapaDolor from './MapaDolor';
import './mis-pautas-paciente.css';

/**
 * MisPautasPaciente — RÉPLICA de la pestaña «Mis pautas» de FisioVisión,
 * reconstruida nativa en el CRM (no se copia el código: se replica el
 * comportamiento respetando el CSS de marca · PROMPT_PESTANA_PAUTAS_COMPLETO.md).
 *
 * FUENTE de datos: el ADMINISTRADOR del CRM (endpoint /fisiovision/para-el-paciente),
 * en el MISMO formato que pautas/para-el-paciente.json del prototipo.
 *
 * Milestone 1 (esto): titular con fecha del reloj · fila de momentos + píldora
 * AHORA · quesito del día · EL TREN (5 estados, índice real, cuadraditos por
 * getBoundingClientRect, vacío con «Ver todo») · el listado. La ficha del
 * ejercicio (§6-8) es el siguiente paso.
 */

// §2/§9 · Los momentos del día · UNA sola tabla (como el prototipo).
const DEF_MOMENTOS = {
  manana: { mom: 'Por la mañana', hasta: 14 },
  tarde: { mom: 'Por la tarde', hasta: 20 },
  noche: { mom: 'Antes de dormir', hasta: 24 },
};
const ORDEN_MOMENTOS = ['manana', 'tarde', 'noche'];
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio',
  'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

const svgCheck = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12l5 5L20 6" /></svg>
);
const svgRaya = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round"><path d="M5 12h14" /></svg>
);
const svgLista = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01" /></svg>
);
const svgFlechaTren = (
  <svg className="fv-ruta__flecha" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
);

// Iconos de línea (§7 «sin emojis · SVG de línea»). Tamaños del prompt.
const _svg = (w, h, path, extra = {}) => (
  <svg width={w} height={h} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={extra.sw || 2} strokeLinecap="round" strokeLinejoin="round">{path}</svg>
);
const ICO = {
  ficha: _svg(15, 15, <><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M8 8h8M8 12h8M8 16h5" /></>),
  seq: _svg(15, 15, <><rect x="3" y="5" width="7" height="14" rx="1.5" /><rect x="14" y="5" width="7" height="14" rx="1.5" /></>),
  animar: _svg(15, 15, <><circle cx="12" cy="12" r="9" /><path d="M10 9l5 3-5 3z" fill="currentColor" stroke="none" /></>),
  video: _svg(15, 15, <><rect x="3" y="6" width="13" height="12" rx="2" /><path d="M16 10l5-3v10l-5-3z" /></>),
  pdf: _svg(15, 15, <><path d="M12 3v12M7 11l5 4 5-4M5 21h14" /></>),
  todo: _svg(15, 15, <path d="M4 6h16M4 12h16M4 18h16" />),
  rot: _svg(13, 13, <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>),
  escudo: _svg(19, 19, <><path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z" /><path d="M12 9v4M12 16h.01" /></>, { sw: 2 }),
  check15: _svg(15, 15, <path d="M4 12l5 5L20 6" />, { sw: 2.4 }),
  ondas: _svg(18, 18, <path d="M3 9c3-3 6 3 9 0s6-3 9 0M3 15c3-3 6 3 9 0s6-3 9 0" />),
  diana: _svg(15, 15, <><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="3" /></>),
  llave: _svg(15, 15, <><circle cx="8" cy="12" r="4" /><path d="M12 12h9M18 12v4" /></>),
  aviso: _svg(15, 15, <><path d="M12 4l9 16H3z" /><path d="M12 10v4M12 17h.01" /></>),
  medialuna: _svg(24, 24, <path d="M20 14a8 8 0 1 1-9-11 7 7 0 0 0 9 11z" />, { sw: 2 }),
  check32: _svg(32, 32, <path d="M4 12l5 5L20 6" />, { sw: 3.2 }),
  pulgar: _svg(24, 24, <path d="M10 4v6H4v10h6l4-2h4a2 2 0 0 0 2-2l1-6a2 2 0 0 0-2-2h-5V4a2 2 0 0 0-4 0z" transform="rotate(180 12 12)" />, { sw: 2 }),
  stop: _svg(30, 30, <><circle cx="12" cy="12" r="9" /><path d="M12 8v5" /><circle cx="12" cy="16.5" r="1" fill="currentColor" stroke="none" /></>, { sw: 2.6 }),
  flechaDcha: _svg(16, 16, <path d="M9 6l6 6-6 6" />, { sw: 2.6 }),
};

// Días de la pauta en bonito: acentos + «y» antes del último («lunes, miércoles y viernes»).
const DIAS_BONITO = { lunes: 'lunes', martes: 'martes', miercoles: 'miércoles', jueves: 'jueves', viernes: 'viernes', sabado: 'sábado', domingo: 'domingo' };
const fmtDias = (arr) => {
  if (!arr || !arr.length) return '';
  if (arr.includes('todos los días')) return 'todos los días';
  const b = arr.map((d) => DIAS_BONITO[d] || d);
  return b.length <= 1 ? b.join('') : `${b.slice(0, -1).join(', ')} y ${b[b.length - 1]}`;
};

const MisPautasPaciente = ({ onCerrar, endpoint = '/clinica/fisiovision/para-el-paciente', datosPdf = {}, fisio = null }) => {
  const [cargando, setCargando] = useState(true);
  const [ejercicios, setEjercicios] = useState([]);
  const [pautaId, setPautaId] = useState('');
  const [pasos, setPasos] = useState({});
  const [diasPauta, setDiasPauta] = useState([]);  // días que SÍ tiene la pauta (para el día que hoy no toca)
  const [momento, setMomento] = useState('todo');   // §9 · al entrar, «Ver todo»
  const [estados, setEstados] = useState([]);        // '' | 'hecha' | 'parada' | 'media'
  const [abierto, setAbierto] = useState(null);      // índice REAL del ejercicio abierto
  const [cuadritos, setCuadritos] = useState(false); // el tren en cuadraditos
  const [vista, setVista] = useState('ficha');       // ficha | secuencia | animar | video | todo
  const [pasoActivo, setPasoActivo] = useState(1);   // §7 · paso seleccionado en secuencia
  const [mapaAbierto, setMapaAbierto] = useState(false); // §8.3 · el mapa del dolor
  // ¿QUÉ HA PASADO? · se abre al marcar «a medias» o «no he podido».
  const [porqueDe, setPorqueDe] = useState(null);   // índice del ejercicio, o null
  const [motivos, setMotivos] = useState({});       // por qué paró cada uno
  const [reproduciendo, setReproduciendo] = useState(false); // §7bis.4 · play de la secuencia
  const [animarPlay, setAnimarPlay] = useState(false);       // §7bis.7 · play grande de Animar
  const rutaRef = useRef(null);
  const aroRef = useRef(null);
  const printRef = useRef(null);  // capa oculta #para-imprimir del PDF

  // Al abrir/cambiar de ejercicio, la ficha arranca en «Ficha» (§9).
  useEffect(() => { setVista('ficha'); setPasoActivo(1); setMapaAbierto(false); setReproduciendo(false); setAnimarPlay(false); }, [abierto]);
  // Al cambiar de vista, se para la reproducción de la secuencia y el vídeo de Animar.
  useEffect(() => { setReproduciendo(false); setAnimarPlay(false); }, [vista]);

  // §7bis.4 · el play pasa las láminas DOS vueltas (900ms cada paso) y el aro
  // se llena UNA vez en ese tiempo (la vuelta del aro = la rutina entera).
  useEffect(() => {
    if (abierto === null || abierto === 'resumen') return;
    const ps = pasos[String(abierto)] || {};
    const nums = Object.keys(ps).map(Number).sort((a, b) => a - b);
    const aro = aroRef.current;
    if (!reproduciendo || nums.length < 2) {
      if (aro) { aro.style.transition = 'none'; aro.style.strokeDashoffset = '106.8'; }
      return;
    }
    const total = nums.length * 2;
    if (aro) {
      aro.style.transition = 'none'; aro.style.strokeDashoffset = '106.8';
      setTimeout(() => { aro.style.transition = `stroke-dashoffset ${total * 900}ms linear`; aro.style.strokeDashoffset = '0'; }, 20);
    }
    setPasoActivo(nums[0]);
    let i = 1;
    const iv = setInterval(() => {
      if (i >= total) { clearInterval(iv); setReproduciendo(false); return; }
      setPasoActivo(nums[i % nums.length]);
      i++;
    }, 900);
    return () => clearInterval(iv);
  }, [reproduciendo, abierto]);

  // Fecha del reloj del sistema (nunca escrita a mano · §0/§9).
  const fechaHoy = useMemo(() => {
    const h = new Date();
    const dias = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
    const t = `${dias[h.getDay()]}, ${h.getDate()} de ${MESES[h.getMonth()]} de ${h.getFullYear()}`;
    return t.charAt(0).toUpperCase() + t.slice(1);
  }, []);

  useEffect(() => {
    let vivo = true;
    axios.get(`${API}${endpoint}`)
      .then((r) => {
        if (!vivo) return;
        const d = r.data || {};
        const ejs = Array.isArray(d.ejercicios) ? d.ejercicios : [];
        setEjercicios(ejs);
        setPautaId((r.data && r.data.pauta_id) || '');
        setPasos(d.pasos || {});
        setDiasPauta(Array.isArray(d.dias_pauta) ? d.dias_pauta : []);

        /* ── LO QUE YA MARCÓ HOY ──────────────────────────────────────────
           Antes esto arrancaba SIEMPRE vacío: `setEstados(ejs.map(() => ''))`.
           Las marcas SÍ se guardaban —y su fisio las veía— pero al salir de
           «Mis pautas» y volver, la pantalla las pedía otra vez como si no
           hubiera hecho nada. Para ella no es un detalle: es que su trabajo
           no ha servido. (Ann, 28-ago-2026.)

           Se leen del calendario, que ya las devuelve por (momento, idx,
           fecha). No hace falta tocar el servidor.

           Se piden DESPUÉS de pintar los ejercicios: si el calendario falla,
           la pauta se ve igual —solo que sin marcas— en vez de quedarse en
           blanco por una petición secundaria. */
        setEstados(ejs.map(() => ''));
        const hoyISO = new Date();
        const HOY = `${hoyISO.getFullYear()}-${String(hoyISO.getMonth() + 1).padStart(2, '0')}-${String(hoyISO.getDate()).padStart(2, '0')}`;
        axios.get(`${API}/paciente/pautas/calendario`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        }).then((rc) => {
          if (!vivo) return;
          const celdas = (rc.data && rc.data.celdas) || [];
          const deHoy = new Map();
          const dolorDe = new Map();
          celdas.forEach((c) => {
            if (c.fecha !== HOY) return;
            let est = c.estado || (c.hecho ? 'hecha' : '');
            // El servidor devuelve «dolor» cuando lo dejó a medias o paró POR
            // DOLOR. Aquí se separan otra vez las dos cosas —cuánto hizo y por
            // qué— para que al volver siga saliendo en rojo y con su palabra.
            if (est === 'dolor') { est = 'media'; dolorDe.set(`${c.momento}|${c.idx}`, true); }
            if (est) deHoy.set(`${c.momento}|${c.idx}`, est);
          });
          if (!deHoy.size) return;
          setEstados(ejs.map((e) => deHoy.get(`${e.mom_key}|${e.idx_momento || 0}`) || ''));
          const mm = {};
          ejs.forEach((e, k) => { if (dolorDe.get(`${e.mom_key}|${e.idx_momento || 0}`)) mm[k] = 'dolor'; });
          if (Object.keys(mm).length) setMotivos((m) => ({ ...m, ...mm }));
        }).catch(() => { /* sin calendario, la pauta se ve igual */ });
      })
      .catch(() => { if (vivo) { setEjercicios([]); } })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, []);

  // §4 · SE MIDE EL MARCO, NO LA VENTANA (getBoundingClientRect, no media query).
  useEffect(() => {
    const el = rutaRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const medir = () => {
      const w = el.getBoundingClientRect().width;
      if (w) setCuadritos(w < 900);
    };
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    medir();
    return () => ro.disconnect();
  }, [cargando, momento]);

  // De qué momento es cada ejercicio (clave de DEF_MOMENTOS).
  const momentoDe = (i) => {
    const m = ejercicios[i] && ejercicios[i].mom;
    for (const k of ORDEN_MOMENTOS) if (DEF_MOMENTOS[k].mom === m) return k;
    return 'manana';
  };
  // Índices REALES de los ejercicios de un momento (o todos si 'todo').
  /* ¿ESTO ES UNA PARADA POR DOLOR? · UNA sola regla para toda la pantalla.
     Ann: «si se marca una pauta por dolor en rojo, luego debe guardarse en el
     resumen y en todas partes como tal». Y "a medias por dolor" ES por dolor:
     en turquesa y sin la palabra parece un «casi lo consigues», y no lo es --
     es la señal que su fisio tiene que ver.
     Estaba escrita SOLO en el panel de confirmacion y la tarjeta del ejercicio
     no la consultaba: un ejercicio dejado a medias por dolor salia SIN COLOR.
     La misma regla en dos sitios y solo uno la sabia. */
  const esDolor = (i, st) => st === 'parada' || (st === 'media' && motivos[i] === 'dolor');

  const indicesDe = (m) => {
    const out = [];
    for (let k = 0; k < ejercicios.length; k++) {
      if (m === 'todo' || momentoDe(k) === m) out.push(k);
    }
    return out;
  };
  // Los momentos que el fisio ha PROGRAMADO (los que tienen ejercicios).
  const momentosProgramados = ORDEN_MOMENTOS.filter((m) => indicesDe(m).length > 0);
  // El momento de la hora actual (para la píldora AHORA).
  const momentoDeAhora = () => {
    const h = new Date().getHours();
    for (const k of ORDEN_MOMENTOS) if (h < DEF_MOMENTOS[k].hasta) return k;
    return 'noche';
  };
  const ahora = momentoDeAhora();

  const abrir = (i) => setAbierto(i);
  const volverALista = () => setAbierto(null);

  /* ══════════════════════════════════════════════════════════════════
     LOS SIETE MOTIVOS · copiados del prototipo, palabra por palabra.
     Cada motivo dice lo que va a hacer su fisio: si el paciente no ve
     PARA QUÉ sirve contestar, miente o no contesta.

     ⚠️ Las palabras están elegidas. «Me da pereza» no lo pulsa nadie:
     suena a confesión y lo lee su fisio. «No tenía ganas» dice lo mismo
     y sí se pulsa. Ninguna opción puede llevar reproche: en cuanto una
     lista juzga, la gente miente y el dato deja de valer.
     NO se reescriben ni se «mejoran».
     ══════════════════════════════════════════════════════════════════ */
  /* La columna `v` (lo que hace el fisio) YA NO SE PINTA — Ann la quitó el
     28-ago-2026 — pero se conserva aquí porque son SUS palabras y describen
     lo que de verdad pasa con cada motivo. Borrarla sería tirar el criterio. */
  const PORQUES = [
    { k: 'dolor',   t: 'Me dolía',                   v: 'Tu fisio lo verá hoy mismo' },
    { k: 'cansada', t: 'No tenía fuerzas',           v: 'Se te baja la carga' },
    { k: 'tiempo',  t: 'No tenía tiempo',            v: 'Te haremos una pauta más corta' },
    { k: 'olvido',  t: 'Se me olvidó',               v: 'Te lo recordamos a tu hora' },
    { k: 'ganas',   t: 'No tenía ganas',             v: 'Normal. Se puede empezar más suave' },
    { k: 'duda',    t: 'No sabía si lo hacía bien',  v: 'Tu fisio te lo explica otra vez' },
    { k: 'sitio',   t: 'No tenía sitio ni material', v: 'Te lo cambiamos por otro que puedas' },
  ];
  // Al elegir motivo se GUARDA. Y si es dolor, se pasa al mapa: eso es clínico.
  const elegirPorque = (i, k) => {
    setMotivos((m) => ({ ...m, [i]: k }));
    setPorqueDe(null);
    if (k === 'dolor') { setMapaAbierto(true); return; }
    setMapaAbierto(false);
    guardarMarca(i, estados[i], PORQUES.find((x) => x.k === k)?.t || '');
  };

  /* Guarda en el servidor lo que ha contestado. Antes esto NO existía: la
     marca solo cambiaba de color en la pantalla, al recargar se perdía, su
     fisio no se enteraba y el calendario nunca se pintaba de color. */
  const guardarMarca = (i, st, motivo) => {
    const e = ejercicios[i] || {};
    if (!pautaId || e.mom_key === undefined) return;
    const hoy = new Date();
    const fecha = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-${String(hoy.getDate()).padStart(2, '0')}`;
    axios.post(`${API}/paciente/pautas/calendario/marcar`, {
      pauta_id: pautaId, momento: e.mom_key, ejercicio: e.idx_momento || 0,
      fecha, hecho: st === 'hecha', estado: st, motivo: motivo || '',
    }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .catch(() => { /* si falla, no se dice que se guardó */ });
  };

  // §8 · marcar cómo ha ido · SOLO desde dentro del ejercicio.
  const marcar = (i, tipo) => {
    const st = tipo === 'entera' ? 'hecha' : tipo === 'mitad' ? 'media' : 'parada';
    setEstados((es) => es.map((x, k) => (k === i ? st : x)));
    // Se guarda en el servidor · ver guardarMarca()
    guardarMarca(i, st, '');
    // a medias / no he podido → primero «¿Qué ha pasado?». El mapa del dolor
    // viene DESPUÉS, y solo si el motivo es dolor: es una pregunta clínica y no
    // se le hace a quien simplemente no tuvo tiempo.
    setPorqueDe(tipo !== 'entera' ? i : null);
    setMapaAbierto(false);
    // §8.1 · celebración SOLO al marcar entera. El GRANDE (2 cañones) si con
    // este cierras el momento; el normal (1 cañón) si aún quedan. «A medias» o
    // «no he podido» → NADA (no se celebra lo que no se hizo).
    if (tipo === 'entera') {
      const IX = indicesDe(momentoDe(i));
      const quedan = IX.filter((k) => k !== i && estados[k] !== 'hecha').length;
      celebrar(quedan === 0);
    }
  };
  // El siguiente que quede por hacer, dentro del mismo momento (da la vuelta).
  const siguientePendiente = (i) => {
    const m = momentoDe(i), IX = indicesDe(m), p = IX.indexOf(i);
    for (let d = 1; d <= IX.length; d++) {
      const k = IX[(p + d) % IX.length];
      if (k !== i && !estados[k]) return k;
    }
    return -1;
  };

  // ── Quesito · un trozo por ejercicio del DÍA · dice cuántos llevas ──
  const quesito = () => {
    const dia = indicesDe('todo');
    const hechos = dia.filter((i) => estados[i] === 'hecha').length;
    // §3.2 · el GRANDE (sector anular, viewBox 140), envuelto en fv-queso-mini.
    const trozos = trozosGrande(
      dia.map((i) => estados[i]),
      dia.map((i) => momentoDe(i)),
      dia.map((i) => ejercicios[i] && ejercicios[i].n),
    );
    return (
      <div className="fv-queso-mini"><div className="fv-queso">
        <svg viewBox="0 0 140 140" role="img" aria-label={`Llevas ${hechos} de ${dia.length} ejercicios`}>
          {trozos.map((t, k) => (
            <path key={k} className={'fv-queso__trozo ' + t.cls} d={t.d}><title>{t.titulo}</title></path>
          ))}
          <text className="fv-queso__n" x="70" y="80">{hechos}</text>
          <text className="fv-queso__de" x="70" y="100">de {dia.length}</text>
        </svg>
      </div></div>
    );
  };

  // ── El tren ──
  const tren = () => {
    if (momento === 'todo') return <div className="fv-ruta" ref={rutaRef} />; // §4 · vacío
    const IX = indicesDe(momento);
    const indicador = (i, pos) => {
      const st = estados[i];
      if (st === 'hecha') return svgCheck;
      if (st === 'media') return svgRaya;
      if (st === 'parada') return svgCheck;
      return pos;
    };
    const estadoTxt = (i) => {
      const st = estados[i];
      if (st === 'hecha') return 'Hecho';
      if (st === 'media') return 'A medias';
      if (st === 'parada') return 'Paraste';
      return 'Pendiente';
    };
    const claseEstado = (i) => {
      const st = estados[i];
      if (st === 'parada') return 'is-parada';
      if (st === 'hecha') return 'is-hecho';
      return '';
    };
    return (
      <div className={'fv-ruta' + (cuadritos ? ' es-cuadritos' : '')} ref={rutaRef}>
        {IX.map((i, pos) => (
          <React.Fragment key={i}>
            <button
              type="button"
              className={'boton_tren ' + claseEstado(i) + (abierto === i ? ' es-vista' : '') + (cuadritos ? ' es-cuadrito' : '')}
              onClick={() => abrir(i)}          /* §4 · navega por el ÍNDICE REAL */
              title={ejercicios[i].n}
            >
              <span className="boton_tren__n">{indicador(i, pos + 1)}</span>
              <span className="boton_tren__cuerpo">
                <span className="boton_tren__tit">{ejercicios[i].n}</span>
                <span className="boton_tren__estado">{estadoTxt(i)}</span>
              </span>
            </button>
            {(pos < IX.length - 1 || true) && !cuadritos && svgFlechaTren}
          </React.Fragment>
        ))}
        {/* Resumen · SIEMPRE el último */}
        <button
          type="button"
          className={'boton_tren boton_tren_resumen' + (abierto === 'resumen' ? ' es-vista' : '') + (cuadritos ? ' es-cuadrito' : '')}
          onClick={() => setAbierto('resumen')}
          title="Resumen de hoy"
        >
          <span className="boton_tren__n">{svgLista}</span>
          <span className="boton_tren__cuerpo">
            <span className="boton_tren__tit">Resumen</span>
            <span className="boton_tren__estado">De hoy</span>
          </span>
        </button>
      </div>
    );
  };

  // ── El listado ──
  const listado = () => {
    const IX = indicesDe(momento);
    return IX.map((i) => {
      const e = ejercicios[i];
      const st = estados[i];
      const cls = 'fv-tarjeta-ej' + (st === 'hecha' ? ' es-hecha' : esDolor(i, st) ? ' es-parada' : (momentoDe(i) === ahora ? ' es-ahora' : ''));
      const ps = pasos[String(i)] || {};
      const primera = ps['1'];
      const dosis = [e.s && `${e.s}×${e.r}`, e.descanso].filter(Boolean).join(' · ');
      const nPasos = Object.keys(ps).length;
      return (
        <div className={cls} key={i}>
          <button type="button" className="fv-tarjeta-ej__cab" onClick={() => abrir(i)}>
            <span className="fv-tarjeta-ej__n">{indicesDe('todo').indexOf(i) + 1}</span>
            <span className="fv-tarjeta-ej__mini">
              {primera && primera.foto ? <img src={primera.foto} alt="" /> : null}
            </span>
            <span className="fv-tarjeta-ej__que">
              <span className="fv-tarjeta-ej__mom">{e.mom}</span>
              <span className="fv-tarjeta-ej__tit">{e.n}</span>
              <span className="fv-tarjeta-ej__datos">
                {dosis}{dosis && nPasos ? ' · ' : ''}{nPasos ? `${nPasos} paso${nPasos === 1 ? '' : 's'}` : ''}
              </span>
              {st && <span className="fv-tarjeta-ej__estado">{st === 'hecha' ? 'Hecho' : st === 'parada' ? 'Paraste' : 'A medias'}</span>}
            </span>
            <svg className="fv-tarjeta-ej__fl" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
          </button>
        </div>
      );
    });
  };

  // ── LA FICHA DE UN EJERCICIO (§6-8) ──
  const ficha = (i) => {
    const e = ejercicios[i];
    if (!e) return null;
    const ps = pasos[String(i)] || {};
    const nums = Object.keys(ps).map(Number).sort((a, b) => a - b);
    const hayPasos = nums.length >= 1;
    const haySecuencia = nums.length >= 2;      // §2 · menos de 2 pasos → no hay secuencia
    const hayVideo = !!e.video;
    const hayAnim = !!e.animacion;
    const st = estados[i];
    const pasoAct = ps[String(pasoActivo)] || ps[String(nums[0])] || null;
    const columnaIzq = hayPasos || hayVideo;    // §6.1 · sin pasos ni vídeo → no se pinta
    const pos = indicesDe('todo').indexOf(i) + 1;

    // Barra de mandos (§7): qué botones salen.
    const vbtn = (id, label, icon, cond) => cond && (
      <button type="button" className={'fv-vbtn' + (vista === id ? ' es-activo' : '')}
        onClick={() => {
          if (id === 'pdf') { pdfDelEjercicio(i); return; } // arma #para-imprimir + window.print()
          setVista(id); if (id === 'secuencia') setPasoActivo(nums[0] || 1);
        }}>
        {icon}<span>{label}</span>
      </button>
    );

    // Columna izquierda.
    const grafica = () => {
      if (vista === 'video' && hayVideo) {
        return (
          <div className="fv-ej__dibujo es-video">
            {/* `poster` NO es decorativo: con preload="none" y sin carátula el
                navegador pinta un rectángulo NEGRO hasta que se pulsa play, y
                parece que el vídeo está roto. (Ann, 28-ago-2026.) */}
            <video className="fv-ej__cine-video" src={e.video} poster={e.thumbnail || undefined}
              controls playsInline preload="none"
              loop={!!e.bucle} controlsList="nodownload noplaybackrate" disablePictureInPicture
              onContextMenu={(ev) => ev.preventDefault()} />
          </div>
        );
      }
      if (vista === 'animar' && hayAnim) {
        return (
          <div className="fv-ej__dibujo es-video">
            <video className="fv-ej__cine-video" src={e.animacion} autoPlay muted playsInline
              loop={e.bucle !== false} controlsList="nodownload noplaybackrate" disablePictureInPicture
              onContextMenu={(ev) => ev.preventDefault()} />
          </div>
        );
      }
      if (vista === 'todo') {
        return (
          <div className="fv-ej__tira">
            {nums.map((k) => {
              const p = ps[String(k)];
              return (
                <div className="fv-ej__tira-paso" key={k}>
                  <div className="fv-ej__tira-num">{k}</div>
                  <div className="fv-ej__tira-cuerpo">
                    {p.foto && <img src={p.foto} alt="" />}
                    {p.tit && <div className="fv-ej__tira-tit">{p.tit}</div>}
                    {p.txt && <div className="fv-ej__tira-txt">{p.txt}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        );
      }
      // ficha / secuencia → un paso
      return (
        <>
          <div className="fv-ej__dibujo">
            {pasoAct && pasoAct.foto
              ? <img className="fv-ej__foto" src={pasoAct.foto} alt="" />
              : <div style={{ color: '#93A0B5', padding: 24, fontSize: 13.5 }}>Sin lámina</div>}
            {e.metodo && <div className="fv-firma-mpd">MÉTODO PILAR DOMÍNGUEZ</div>}
          </div>
          {pasoAct && (pasoAct.tit || pasoAct.txt) && (
            <div className="fv-ej__paso-caja">
              {pasoAct.tit && <div className="fv-ej__paso-tit">{pasoAct.tit}</div>}
              {pasoAct.txt && <div className="fv-ej__paso-txt">{pasoAct.txt}</div>}
            </div>
          )}
        </>
      );
    };

    const dosis = [
      { k: 'Series', v: e.s || '—' },
      { k: 'Repeticiones', v: e.r || '—' },
      { k: 'Descanso', v: e.descanso || '30 s' },
    ];

    // §7bis.7 · ANIMAR · pantalla reducida (fv-simple): las láminas + play grande
    // + título y una frase. El play grande reproduce la ilustración animada.
    const animarView = () => (
      <div className="fv-simple">
        <div className="fv-simple__fotos">
          {animarPlay && hayAnim ? (
            <figure className="fv-simple__foto">
              <video className="fv-simple__video" src={e.animacion} autoPlay muted playsInline
                loop={e.bucle !== false} controlsList="nodownload noplaybackrate" disablePictureInPicture
                onContextMenu={(ev) => ev.preventDefault()} />
            </figure>
          ) : (
            <>
              {nums.map((k) => { const p = ps[String(k)]; return p.foto ? (
                <figure className="fv-simple__foto" key={k}><img src={p.foto} alt="" /></figure>
              ) : null; })}
              <button type="button" className="fv-simple__play" title="Ver el movimiento" onClick={() => setAnimarPlay(true)}>
                <span />
              </button>
            </>
          )}
        </div>
        <div className="fv-simple__tx">
          <h3>{e.n}</h3>
          <p>{e.objetivo || (ps['1'] && ps['1'].txt) || (e.ben && e.ben[0]) || ''}</p>
        </div>
      </div>
    );

    return (
      <div>
        {/* Barra de mandos */}
        <div className="fv-mandos-arriba">
          <div className="fila_pasos">
            <div className="fila_pasos__mandos">
              {vbtn('ficha', 'Ficha', ICO.ficha, true)}
              {vbtn('secuencia', 'Secuencia', ICO.seq, haySecuencia)}
              {vbtn('animar', 'Animar', ICO.animar, hayAnim)}
              {vbtn('video', 'Vídeo', ICO.video, hayVideo)}
              {vbtn('pdf', 'PDF', ICO.pdf, true)}
            </div>
            {/* números · nacen ocultos; solo con «Secuencia» */}
            <div className="fila_pasos__nums" hidden={vista !== 'secuencia'}>
              {nums.map((k) => (
                <button key={k} type="button"
                  className={'boton_numeros' + (pasoActivo === k ? ' es-activo' : '')}
                  onClick={() => { setReproduciendo(false); setPasoActivo(k); }}>{k}</button>
              ))}
              {/* §7bis.4 · el play que pasa las láminas (secuencia animada) · interruptor */}
              <button type="button" title="Ver el movimiento"
                className={'boton_play_secuencia' + (reproduciendo ? ' es-andando' : '')}
                onClick={() => setReproduciendo((v) => !v)}>
                {/* SIN <span> DENTRO, como en FisioVisión: el triángulo lo dibuja
                    el CSS con ::after. Con el span salían DOS triángulos, uno
                    al lado del otro, y parecía un botón de avance rápido (⏩).
                    Ann lo describió exacto: «un icono como duplicado».
                    (28-ago-2026.) */}
                <svg className="boton_play_secuencia__aro" viewBox="0 0 40 40">
                  <circle ref={aroRef} className="boton_play_secuencia__aro-l" cx="20" cy="20" r="17" />
                </svg>
              </button>
              <button type="button" className="fv-vbtn" onClick={() => setVista('todo')} style={{ marginLeft: 6 }}>
                {ICO.todo}<span>Ver todo</span>
              </button>
            </div>
          </div>
        </div>

        {vista === 'animar' ? animarView() : (
        <div className="fv-ej">
          {/* Columna izquierda */}
          {columnaIzq && (
            <div>
              <div className="fv-ej__caja-rot">{ICO.rot}{hayPasos ? 'Cómo se hace, paso a paso' : 'Míralo en vídeo'}</div>
              <div className={'fv-ej__grafica' + (vista === 'video' || vista === 'animar' ? ' es-video' : '') + (vista === 'todo' ? ' es-todo' : '')}>
                {grafica()}
              </div>
            </div>
          )}

          {/* Columna derecha · 14 bloques en orden */}
          <div>
            {/* 1 · cabecera */}
            <div className="fv-ej__cab">
              <span className="fv-ej__num">{pos}</span>
              <span className="fv-ej__tit">{e.n}</span>
            </div>

            {/* 2 · Recomendado para… */}
            {Array.isArray(e.para) && e.para.length > 0 && (
              <div className="fv-para">
                {e.para.map((p, k) => <span className="fv-para__pastilla" key={k}>{p}</span>)}
              </div>
            )}

            {/* 3 · ANTES DE EMPEZAR (ocre, arriba) */}
            {e.seguridad && (
              <div className="fv-ej__seguridad">
                {ICO.escudo}
                <div><b>Antes de empezar</b>{e.seguridad}</div>
              </div>
            )}

            {/* 4 · PARA QUÉ TE SIRVE / instrucciones */}
            {Array.isArray(e.ben) && e.ben.length > 0 ? (
              <div className="fv-ej__benef">
                <div className="fv-ej__benef-rot">Para qué te sirve</div>
                <ul className="fv-ej__benef-lista">
                  {e.ben.map((b, k) => <li key={k}>{ICO.check15}<span>{b}</span></li>)}
                </ul>
              </div>
            ) : e.d ? (
              <div className="fv-ej__instrucciones">{e.d}</div>
            ) : null}

            {/* 5 · Series · Repeticiones · Descanso */}
            <div className="fv-ej__datos">
              {dosis.map((d) => (
                <div className="fv-ej__dato" key={d.k}><small>{d.k}</small><b>{d.v}</b></div>
              ))}
            </div>

            {/* 6 · LA RESPIRACIÓN (azul cielo) */}
            {e.respiracion && (
              <div className="fv-ej__respira">
                {ICO.ondas}
                <div><b>La respiración</b>{e.respiracion}</div>
              </div>
            )}

            {/* 7 · PROGRESIÓN */}
            {e.progresion && (
              <div className="fv-ej__progresion">
                <b>Cuando te resulte fácil</b>{e.progresion}
              </div>
            )}

            {/* 8 · LA GUÍA GUIADA · solo si el ejercicio la lleva marcada.
                Sin campo `programa`, no se pinta nada. */}
            {e.programa && <ProgramaGuiado clave={e.programa} />}

            {/* 9 · Nota del fisio (violeta) · vacío → NO se pinta */}
            {e.nota && (
              <div className="fv-ej__nota">
                {/* la cara de su fisio · sin ella la nota parece del programa */}
                {fisio && fisio.foto && (
                  <img className="fv-ej__nota-foto" src={fisio.foto} alt={fisio.nombre || 'Tu fisioterapeuta'} />
                )}
                <p className="fv-ej__nota-txt">
                  {/* el nombre, en violeta y en negrita: es SU voz */}
                  <b>{(fisio && fisio.nombre ? fisio.nombre.split(' ')[0] : 'Tu fisio') + ':'}</b>{' '}
                  {e.nota}
                </p>
              </div>
            )}

            {/* 10 · Aviso clínico */}
            {e.aviso && <div className="fv-ej__legal">{e.aviso}</div>}

            {/* 11 · sentir · claves · errores */}
            {(e.sentir || (e.claves && e.claves.length) || (e.errores && e.errores.length)) && (
              <div className="fv-tres">
                {e.sentir && (
                  <div className="fv-tres__b fv-tres__b--sentir">
                    <div className="fv-tres__rot">{ICO.diana}¿Dónde debes sentirlo?</div>
                    <div className="fv-tres__sentir"><div>{e.sentir}</div></div>
                  </div>
                )}
                {e.claves && e.claves.length > 0 && (
                  <div className="fv-tres__b fv-tres__b--claves">
                    <div className="fv-tres__rot">{ICO.llave}Claves importantes</div>
                    <ul>{e.claves.map((c, k) => <li key={k}>{c}</li>)}</ul>
                  </div>
                )}
                {e.errores && e.errores.length > 0 && (
                  <div className="fv-tres__b fv-tres__b--errores">
                    <div className="fv-tres__rot">{ICO.aviso}Errores frecuentes</div>
                    <ul>{e.errores.map((c, k) => <li key={k}>{c}</li>)}</ul>
                  </div>
                )}
              </div>
            )}

            {/* 12 · separador */}
            <hr className="fv-ej__sep" />

            {/* 13 · ¿Cómo te ha ido? + 3 redondas (ocultas si ya marcó) */}
            {!st && (
              <>
                <div className="fv-ej__preg">¿Cómo te ha ido?</div>
                <div className="fv-marcar">
                  <button type="button" className="fv-marcar__op fv-marcar__op--mitad" onClick={() => marcar(i, 'mitad')}>
                    <span className="fv-marcar__aro">{ICO.medialuna}</span>
                    <span className="fv-marcar__txt">A medias</span>
                  </button>
                  <button type="button" className="fv-marcar__op fv-marcar__op--entera" onClick={() => marcar(i, 'entera')}>
                    <span className="fv-marcar__aro">{ICO.check32}</span>
                    <span className="fv-marcar__txt">La he hecho entera</span>
                  </button>
                  <button type="button" className="fv-marcar__op fv-marcar__op--nada" onClick={() => marcar(i, 'nada')}>
                    <span className="fv-marcar__aro">{ICO.pulgar}</span>
                    <span className="fv-marcar__txt">No he podido</span>
                  </button>
                </div>
              </>
            )}

            {/* ¿QUÉ HA PASADO? · se abre JUSTO DEBAJO, sin sacar de la pantalla
                el ejercicio, para que nadie se pierda. Textos del prototipo. */}
            {porqueDe === i && (
              <div className="fv-porque on">
                <div className="fv-porque__tit">¿Qué ha pasado?</div>
                <p className="fv-porque__calma">
                  Esto no lo pregunto para controlarte. Lo pregunto para poder cambiar tu pauta.
                </p>
                <div className="fv-porque__lista">
                  {PORQUES.map((m) => (
                    <button type="button" key={m.k}
                      className={'fv-porque__btn' + (m.k === 'dolor' ? ' es-dolor' : '')}
                      onClick={() => elegirPorque(i, m.k)}>
                      {/* SOLO EL MOTIVO · Ann, 28-ago-2026: «todo este texto
                          adicional fuera». Al lado de cada motivo iba lo que
                          haría el fisio («Tu fisio lo verá hoy mismo», «Se te
                          baja la carga»…). Con siete opciones eran catorce
                          frases que leer para pulsar una: el texto de más
                          estorbaba en vez de ayudar. */}
                      <span className="fv-porque__qu">{m.t}</span>
                    </button>
                  ))}
                </div>
                <button type="button" className="fv-porque__atras"
                  onClick={() => setPorqueDe(null)}>Volver</button>
              </div>
            )}

            {/* 14 · aviso de hecho (oculto mientras se marca el dolor o el motivo) */}
            {st && !mapaAbierto && porqueDe !== i && (() => {
              const m = momentoDe(i), IX = indicesDe(m);
              const hechos = IX.filter((k) => estados[k] === 'hecha').length;
              const sig = siguientePendiente(i);
              const parada = st === 'parada';
              /* DEJARLO A MEDIAS POR DOLOR NO ES DEJARLO A MEDIAS Y YA (Ann,
                 28-ago-2026): «cuando dejas a medias algo por dolor debe
                 quedarse de color rojo y decir lo dejaste a medias por dolor».
                 En turquesa y sin la palabra parecía un «casi lo consigues»,
                 y no lo es: es la señal que su fisio tiene que ver. */
              const porDolor = motivos[i] === 'dolor';
              const rojo = esDolor(i, st);   // la MISMA regla, no una copia
              return (
                <div className={'fv-hecho' + (rojo ? ' es-parada' : '')}>
                  <div className="fv-hecho__sello">{rojo ? ICO.stop : ICO.check32}</div>
                  <div className="fv-hecho__tit">{parada ? 'Hiciste bien en parar' : st === 'media' ? (porDolor ? 'Lo dejaste a medias por dolor' : 'Lo dejaste a medias') : `${(ejercicios[i] && ejercicios[i].n) || 'Ejercicio'} hecho`}</div>
                  <div className="fv-hecho__cuenta">
                    {parada ? 'Parar también es parte del tratamiento.' : <>Llevas <b>{hechos}</b> de <b>{IX.length}</b></>}
                  </div>
                  {/* Acabar el MOMENTO no es lo mismo que acabar el DÍA. En el
                      prototipo, cuando ya no queda nada de hoy, se cierra con
                      «¡Has terminado tu pauta de hoy!» y una salida. Aquí solo
                      se decía «este momento» y el día se quedaba sin final:
                      marcabas el último y no pasaba nada. */}
                  {sig < 0
                    ? (() => {
                        const quedanHoy = indicesDe('todo').filter((k) => !estados[k]).length;
                        return quedanHoy === 0 ? (
                          <>
                            <div className="fv-hecho__fin">¡Has terminado tu pauta de hoy!</div>
                            <button type="button" className="fv-hecho__ir"
                              onClick={() => { setAbierto(null); setMomento('todo'); }}>
                              Ver mi resumen {ICO.flechaDcha}
                            </button>
                          </>
                        ) : (
                          <>
                            <div className="fv-hecho__fin">¡Has terminado este momento!</div>
                            <button type="button" className="fv-hecho__ir"
                              onClick={() => { setAbierto(null); setMomento('todo'); }}>
                              Ver lo que queda {ICO.flechaDcha}
                            </button>
                          </>
                        );
                      })()
                    : <>
                        <div className="fv-hecho__sig">Ahora el siguiente:</div>
                        <div className="fv-hecho__nombre">{ejercicios[sig].n}</div>
                        <button type="button" className="fv-hecho__ir" onClick={() => abrir(sig)}>
                          Ir al siguiente {ICO.flechaDcha}
                        </button>
                      </>}
                </div>
              );
            })()}

            {/* §8.3 · el mapa del dolor (a medias / no he podido) */}
            {mapaAbierto && st && st !== 'hecha' && (
              <MapaDolor
                /* ── SE GUARDA ────────────────────────────────────────────
                   Antes esto solo cerraba la ventana: la paciente marcaba
                   dónde le dolía, con cuánta intensidad, pulsaba «Registrar»
                   y SE PERDÍA. Hacía el trabajo para nada y su fisio no se
                   enteraba nunca. Va cifrado en reposo: es dato de salud. */
                onRegistrar={(d) => {
                  const e = ejercicios[i] || {};
                  axios.post(`${API}/paciente/mapa-dolor`, {
                    pauta_id: pautaId,
                    ejercicio: e.n || '',
                    momento: e.mom_key || '',
                    motivo: (d && d.motivo) || '',
                    zonas: (d && d.zonas) || [],
                    eva: (d && d.eva) != null ? d.eva : null,
                  }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
                    .catch(() => { /* si falla, no se dice que se guardó */ });
                  // El motivo NO sale del mapa: sale de los siete porqués, y
                  // aquí es siempre «Me dolía» (el mapa solo se abre por ahí).
                  // Antes se guardaba `d.motivo` y, al quitar la lista de
                  // motivos del mapa, habría escrito un motivo VACÍO encima
                  // del que la paciente ya había elegido.
                  guardarMarca(i, estados[i], PORQUES.find((x) => x.k === 'dolor')?.t || 'Me dolía');
                  setMapaAbierto(false);
                }}
                onCancelar={() => { setEstados((es) => es.map((x, k) => (k === i ? '' : x))); setMapaAbierto(false); }}
              />
            )}
          </div>
        </div>
        )}
      </div>
    );
  };

  // ── §8.4 · EL RESUMEN DEL DÍA (tercera vista) ──
  const resumenView = () => {
    const dia = indicesDe('todo');
    const total = dia.length;
    const hechos = dia.filter((i) => estados[i] === 'hecha').length;
    const parados = dia.filter((i) => estados[i] === 'parada').length;
    const hoy = new Date();
    const fechaTxt = `${hoy.getDate()} de ${MESES[hoy.getMonth()]}`;
    // frase de arriba (3 versiones · §8.4.1)
    const frase = (total > 0 && hechos === total)
      ? <><b>Has hecho toda tu pauta</b> hoy, {fechaTxt}. Thais lo verá.</>
      : (hechos === 0 && parados === 0)
        ? <>Todavía no has hecho ninguno hoy, {fechaTxt}. Cuando quieras.</>
        : <>Esto es lo que llevas hoy, {fechaTxt}.</>;
    // informe (§8.4.3, versión núcleo, sin culpabilizar)
    let informe;
    if (total > 0 && hechos === total) informe = 'Has hecho toda tu pauta. Thais lo verá.';
    else if (hechos === 0 && parados === 0) informe = 'Hoy todavía no has empezado. Con el primero basta para que el día cuente.';
    else informe = `Llevas ${hechos} de ${total}.`;
    // quesito GRANDE con leyenda (§3.2.3)
    // el quesito tambien: un trozo «a medias por dolor» cuenta como parada.
    const trozos = trozosGrande(dia.map((i) => (esDolor(i, estados[i]) ? 'parada' : estados[i])), dia.map((i) => momentoDe(i)), dia.map((i) => ejercicios[i] && ejercicios[i].n));
    // filas agrupadas por momentos (§8.4.4)
    const momentos = ORDEN_MOMENTOS.filter((m) => indicesDe(m).length > 0);
    const filaEstado = (i) => {
      const st = estados[i];
      if (st === 'hecha') return { cls: 'es-hecha', pct: 'es-full', txt: 'La hiciste', small: null };
      if (st === 'parada') return { cls: 'es-parada', pct: 'es-parada', txt: 'Paraste', small: 'No pudiste hacerla' };
      // «y luego debe guardarse en el RESUMEN y en todas partes como tal» (Ann).
      // El color ya era rojo, pero la palabra no lo decia: ponia «A medias» a
      // secas y el motivo se perdia justo en la pantalla que resume el dia.
      if (st === 'media') return esDolor(i, st)
        ? { cls: 'es-parada', pct: 'es-parada', txt: 'A medias por dolor', small: 'La dejaste a medias porque te dolía' }
        : { cls: 'es-parada', pct: 'es-parada', txt: 'A medias', small: 'La dejaste a medias' };
      return { cls: '', pct: 'es-cero', txt: 'Todavía sin hacer', small: null };
    };
    return (
      <div className="fv-resumen">
        <div className="fv-resumen__titulo">Resumen de hoy</div>
        <div className="fv-cabresumen">
          <div className="fv-cabresumen__izq">
            <div className="fv-informe">
              <div className="fv-informe__rot">Lo que veo hoy</div>
              <p>{informe}</p>
            </div>
          </div>
          <div className="fv-queso-grande">
            <svg viewBox="0 0 140 140" role="img" aria-label={`Llevas ${hechos} de ${total} ejercicios`}>
              {trozos.map((t, k) => <path key={k} className={'fv-queso__trozo ' + t.cls} d={t.d}><title>{t.titulo}</title></path>)}
              <text className="fv-queso__n" x="70" y="80">{hechos}</text>
              <text className="fv-queso__de" x="70" y="100">de {total}</text>
            </svg>
            <div className="fv-queso__pie">
              <span><i className="es-hecho" />Hechos</span>
              <span><i className="es-parada" />No acabados</span>
              <span><i className="es-falta" />Te faltan</span>
            </div>
          </div>
        </div>

        <p style={{ fontSize: 15.5, lineHeight: 1.55, color: '#6B778F', margin: '0 0 16px' }}>{frase}</p>

        {momentos.map((m) => {
          const IX = indicesDe(m);
          const h = IX.filter((i) => estados[i] === 'hecha').length;
          return (
            <div className="fv-resumen__bloque" key={m}>
              <div className="fv-resumen__cab">
                <span className="fv-resumen__rot">{DEF_MOMENTOS[m].mom}</span>
                <span className={'fv-resumen__cuenta' + (h === IX.length ? ' es-completo' : '')}>{h} de {IX.length}</span>
              </div>
              {IX.map((i) => {
                const e = ejercicios[i]; const ps = pasos[String(i)] || {}; const foto = ps['1'] && ps['1'].foto;
                const fe = filaEstado(i);
                return (
                  <div className={'fv-dias__fila ' + fe.cls} key={i}>
                    {foto ? <img className="fv-dias__foto" src={foto} alt="" /> : <span className="fv-dias__ico">{ICO.rot}</span>}
                    <span className="fv-dias__ej">{e.n}{fe.small && <small>{fe.small}</small>}</span>
                    <span className={'fv-dias__pct ' + fe.pct}>{fe.txt}</span>
                  </div>
                );
              })}
            </div>
          );
        })}

        {/* AQUÍ ESTABA «¿TE HA DOLIDO HOY?» (No / Un poco / Bastante / Mucho).
            RETIRADO · Ann, 28-ago-2026: «se contradice con todo lo que hemos
            hecho de dolor». El dolor se mide con el EVA oficial (0-100); una
            segunda escala informal en paralelo da dos verdades del mismo dato.

            Y había algo peor, que se ve al mirar el código: al pulsar decía
            «Apuntado. Thais lo verá» y NO SE GUARDABA EN NINGÚN SITIO. Era
            estado de pantalla y nada más: la paciente contestaba, se le
            prometía que su fisio lo vería, y se perdía al cerrar. */}
      </div>
    );
  };

  // Cargar la fuente Poppins de FisioVisión (marca) para que la réplica sea fiel.
  useEffect(() => {
    const id = 'fvpac-poppins';
    if (document.getElementById(id)) return;
    const l = document.createElement('link');
    l.id = id; l.rel = 'stylesheet';
    l.href = 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap';
    document.head.appendChild(l);
  }, []);

  // ── EL PDF (ESPEC_PDF_DE_LA_PAUTA.md) · SIN librería: se arma HTML en la capa
  // oculta #para-imprimir y se llama a window.print() (el navegador ofrece «Guardar
  // como PDF»). Mismas fotos/textos que la pantalla. El setTimeout(60) da tiempo al
  // navegador a pintar antes de imprimir (si no, sale hoja en blanco, sin error). ──
  const escHtml = (s) => String(s == null ? '' : s).replace(/[&<>"]/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const cabeceraPdf = () => {
    const d = datosPdf || {};
    const hoy = new Date().toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
    const der = [d.zona, hoy, [d.clinica, d.fisio].filter(Boolean).join(' · ')].filter(Boolean)
      .map((x) => escHtml(x)).join('<br>');
    return `<div class="fv-print__cab"><div class="fv-print__marca"><b>FisioVisión</b>`
      + `<small>Fisioterapia con visión</small></div><div class="fv-print__quien">`
      + `<b>${escHtml(d.paciente || '')}</b><br>${der}</div></div>`;
  };
  const piePdf = () => {
    const d = datosPdf || {};
    return `<div class="fv-print__pie"><div>Si algo te duele, para y díselo a tu fisioterapeuta.<br>`
      + `<b>Esta pauta es solo para ${escHtml(d.paciente || 'ti')}: no la sigas si no es la tuya.</b>`
      + `</div><div><b>FisioVisión</b></div></div>`;
  };
  const bloqueEjercicioPdf = (i, orden) => {
    const e = ejercicios[i]; if (!e) return '';
    const ps = pasos[String(i)] || {};
    const nums = Object.keys(ps).map(Number).sort((a, b) => a - b);
    const dosis = [e.mom, e.s && `${e.s} series`, e.r && `${e.r} repeticiones`].filter(Boolean).join(' · ');
    const para = (Array.isArray(e.para) ? e.para : []).map((p) => escHtml(String(p).toLowerCase())).join(' · ');
    const benef = (Array.isArray(e.ben) ? e.ben : []).map(escHtml).join(' · ');
    const pasosHtml = nums.length
      ? `<div class="fv-print__pasos">${nums.map((k) => { const p = ps[String(k)] || {}; return `<div class="fv-print__paso">${p.foto ? `<img src="${escHtml(p.foto)}" alt="">` : ''}<div class="fv-print__paso-tx"><b>${k}.</b> ${escHtml(p.tit || '')} ${escHtml(p.txt || '')}</div></div>`; }).join('')}</div>`
      : (e.d ? `<div class="fv-print__benef">${escHtml(e.d)}</div>` : '');
    return `<div class="fv-print__ej"><div class="fv-print__ej-cab">`
      + `<span class="fv-print__num">${orden}</span><span class="fv-print__tit">${escHtml(e.n || '')}</span>`
      + `${e.metodo ? '<span class="fv-print__metodo">Método Pilar Domínguez®</span>' : ''}</div>`
      + `${para ? `<div class="fv-print__para">Recomendado para ${para}</div>` : ''}`
      + `${dosis ? `<div class="fv-print__dosis">${escHtml(dosis)}</div>` : ''}`
      + `${benef ? `<div class="fv-print__benef">Para qué te sirve: ${benef}</div>` : ''}`
      + `${pasosHtml}`
      + `${e.nota ? `<div class="fv-print__nota">${escHtml(e.nota)}</div>` : ''}</div>`;
  };
  const imprimir = (html) => {
    const caja = printRef.current;
    if (!caja) return;
    caja.innerHTML = `${cabeceraPdf()}${html}${piePdf()}`;
    setTimeout(() => window.print(), 60);
  };
  const pdfDelEjercicio = (i) => imprimir(bloqueEjercicioPdf(i, indicesDe('todo').indexOf(i) + 1));

  return (
    <div className="fvpac">
      {onCerrar && (
        <button type="button" className="fvpac__volver" onClick={onCerrar}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M15 6l-6 6 6 6" /></svg>
          Volver al administrador
        </button>
      )}

      <div className="fvpac__titular">
        <h1>Mi pauta de hoy</h1>
        <span className="fvpac__fecha">{fechaHoy}</span>
      </div>

      {cargando ? (
        <div className="fvpac__cargando">Cargando tu pauta…</div>
      ) : ejercicios.length === 0 ? (
        /* Hoy no toca (o sin pauta): mensaje honesto con los días que SÍ tiene. */
        <div className="fvpac__vacio" style={{ padding: '18px 2px', lineHeight: 1.55 }}>
          {diasPauta.length > 0 ? (
            <>
              <p style={{ margin: '0 0 6px', fontWeight: 700, color: '#0A2860', fontSize: 16 }}>
                Tus pautas son los {fmtDias(diasPauta)}.
              </p>
              <p style={{ margin: 0, color: '#6B778F' }}>
                Hoy tu fisio no te ha puesto ejercicios — descansa, nos vemos el próximo día.
              </p>
            </>
          ) : (
            <p style={{ margin: 0, color: '#6B778F' }}>Aún no tienes una pauta publicada para hoy.</p>
          )}
        </div>
      ) : (
        <>
          {/* Fila de momentos + quesito */}
          <div className="fv-fila-momentos">
            <div className="fv-momentos">
              <button type="button" className={'boton_menu_pauta' + (momento === 'todo' ? ' is-on' : '')} onClick={() => { setMomento('todo'); setAbierto(null); }}>
                Ver todo
              </button>
              {momentosProgramados.map((m) => (
                <button
                  key={m}
                  type="button"
                  className={'boton_menu_pauta' + (momento === m ? ' is-on' : '')}
                  onClick={() => { setMomento(m); setAbierto(null); }}
                >
                  {DEF_MOMENTOS[m].mom}
                  {m === ahora && <span className="fv-momentos__ahora">Ahora</span>}
                </button>
              ))}
            </div>
            {quesito()}
          </div>

          {/* El tren */}
          {tren()}

          {/* Ficha del ejercicio · o el listado */}
          {abierto !== null ? (
            <div>
              <button type="button" className="fvpac__volver" onClick={volverALista} style={{ marginTop: 6 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M15 6l-6 6 6 6" /></svg>
                Volver a la lista
              </button>
              {abierto === 'resumen' ? resumenView() : ficha(abierto)}
            </div>
          ) : (
            <div style={{ marginTop: 4 }}>{listado()}</div>
          )}
        </>
      )}
      {/* Capa oculta del PDF · el JS la rellena y llama a window.print() */}
      {/* LA CAPA DEL PDF CUELGA DE <body>, NO DE AQUÍ (27-ago-2026).
          La regla de impresión apaga `body > *` y esta capa vivía dentro de
          #root, que ES un `body > *`: el padre se apagaba y se llevaba la capa
          por delante → el PDF salía EN BLANCO. Con el portal es hija directa de
          body, y la regla la exceptúa por su clase. */}
      {createPortal(
        <div className="fv-print" id="para-imprimir" aria-hidden="true" ref={printRef} />,
        document.body,
      )}
    </div>
  );
};

export default MisPautasPaciente;
